import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from './client';
import type { Notification, NotificationListEnvelope } from './types';
import { useAuthStore } from '@/stores/auth.store';

/**
 * useNotifications — single source of truth for the In-App Bell.
 *
 * Wraps the unified /api/notifications/* surface (PRD §07) with the
 * exact slice the bell needs:
 *
 *   • Full caller-scoped list (default 20, max 100 per §07.NFR).
 *   • Today / Earlier grouping on top of the list. "Today" = events
 *     with createdAt on or after the local-tz midnight; "Earlier"
 *     = everything else. Matches the orchestrator's scope-prompt
 *     contract.
 *   • unreadCount that drives the badge.
 *   • markRead / markAllRead / dismiss mutations with optimistic
 *     updates so the popover stays open and the row state flips
 *     instantly — pre-PRD §07 AC-IB-002 / AC-IB-004 / AC-IB-005.
 *
 * Refetch policy:
 *   • Polling every 60 s while the tab is focused (the realtime
 *     fallback for AC-IB-008).
 *   • refetchOnWindowFocus = true so a Cmd-Tab back triggers
 *     reconciliation immediately.
 *
 * Cross-tab sync:
 *   • A `BroadcastChannel('notifications')` instance subscribes to
 *     read/dismiss events from peer tabs and invalidates the local
 *     query so both bells stay in sync without waiting for the
 *     60 s poll. Covers PRD §07.8 "Concurrent session in another
 *     tab" edge case. Sender side fires after each mutation.
 */
const QUERY_KEY = ['notifications', 'list'] as const;

function localMidnightISO(d: Date = new Date()): number {
  const midnight = new Date(d);
  midnight.setHours(0, 0, 0, 0);
  return midnight.getTime();
}

/**
 * Get (or create) the shared BroadcastChannel for cross-tab
 * notification sync. Returns null if the runtime lacks support
 * (older Safari, SSR, jsdom in tests) so callers can no-op.
 */
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel('notifications');
  } catch {
    return null;
  }
}

export interface UseNotificationsOptions {
  /** Page size. Defaults to 20 per PRD §07.NFR. Caps to 100 server-side. */
  limit?: number;
  /** Polling interval in ms. Defaults to 60_000. Set to false to disable. */
  refetchIntervalMs?: number | false;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { limit = 20, refetchIntervalMs = 60_000 } = options;
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.userId) ?? 0;

  const query = useQuery({
    queryKey: [...QUERY_KEY, limit],
    queryFn: () => notificationsApi.list(limit),
    enabled: userId > 0,
    refetchInterval: refetchIntervalMs === false ? false : refetchIntervalMs,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const results: Notification[] = useMemo(
    () => (query.data?.results ?? []) as Notification[],
    [query.data],
  );

  const { today, earlier, unreadCount } = useMemo(() => {
    const midnight = localMidnightISO();
    const todayRows: Notification[] = [];
    const earlierRows: Notification[] = [];
    let unread = 0;
    for (const row of results) {
      const created = new Date(row.createdAt).getTime();
      if (Number.isFinite(created) && created >= midnight) todayRows.push(row);
      else earlierRows.push(row);
      if (!row.readAt) unread += 1;
    }
    return { today: todayRows, earlier: earlierRows, unreadCount: unread };
  }, [results]);

  // Local cache mutation helper — preserves the envelope shape so
  // optimistic updates don't break the query consumer.
  function setLocalList(mutator: (rows: Notification[]) => Notification[]) {
    queryClient.setQueryData<NotificationListEnvelope>(
      [...QUERY_KEY, limit],
      (curr) => {
        const rows = mutator(curr?.results ?? []);
        return { count: rows.length, results: rows };
      },
    );
  }

  // Listen for peer-tab read/dismiss events and invalidate.
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;
    const onMessage = (e: MessageEvent) => {
      const t = (e.data as { type?: string })?.type;
      if (t === 'read' || t === 'dismiss' || t === 'mark-all-read') {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      }
    };
    ch.addEventListener('message', onMessage);
    return () => {
      ch.removeEventListener('message', onMessage);
      ch.close();
    };
  }, [queryClient]);

  function broadcast(type: 'read' | 'dismiss' | 'mark-all-read', payload: Record<string, unknown> = {}) {
    const ch = getChannel();
    if (!ch) return;
    try {
      ch.postMessage({ type, ...payload });
    } finally {
      ch.close();
    }
  }

  const markRead = useMutation({
    mutationFn: (notificationId: number) => notificationsApi.markRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NotificationListEnvelope>([...QUERY_KEY, limit]);
      const nowIso = new Date().toISOString();
      setLocalList((rows) =>
        rows.map((r) => (r.notificationId === notificationId ? { ...r, readAt: nowIso } : r)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData([...QUERY_KEY, limit], ctx.previous);
      }
    },
    onSuccess: (_data, notificationId) => {
      broadcast('read', { notificationId });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const dismiss = useMutation({
    mutationFn: (notificationId: number) => notificationsApi.dismiss(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NotificationListEnvelope>([...QUERY_KEY, limit]);
      setLocalList((rows) => rows.filter((r) => r.notificationId !== notificationId));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData([...QUERY_KEY, limit], ctx.previous);
      }
    },
    onSuccess: (_data, notificationId) => {
      broadcast('dismiss', { notificationId });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<NotificationListEnvelope>([...QUERY_KEY, limit]);
      const nowIso = new Date().toISOString();
      setLocalList((rows) => rows.map((r) => (r.readAt ? r : { ...r, readAt: nowIso })));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData([...QUERY_KEY, limit], ctx.previous);
      }
    },
    onSuccess: () => broadcast('mark-all-read'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    /** Raw list, freshest first (server-sorted: unread-first, then newest). */
    results,
    /** Created today (local tz). */
    today,
    /** Created before today's local midnight. */
    earlier,
    unreadCount,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    isMarkingAllRead: markAllRead.isPending,
    dismiss: dismiss.mutate,
  };
}

/**
 * Re-export the today/earlier classifier as a pure function so unit
 * tests can exercise the grouping logic without mounting the hook.
 */
export function groupNotificationsByDay(
  rows: Notification[],
  now: Date = new Date(),
): { today: Notification[]; earlier: Notification[] } {
  const midnight = localMidnightISO(now);
  const today: Notification[] = [];
  const earlier: Notification[] = [];
  for (const r of rows) {
    const t = new Date(r.createdAt).getTime();
    if (Number.isFinite(t) && t >= midnight) today.push(r);
    else earlier.push(r);
  }
  return { today, earlier };
}
